/**
 * Collection Grid Analytics Integration
 * Tracks product clicks with exact grid positions and personalization exposure
 * Integrates with MagnoliaAnalyticsTracker
 */

(function () {
  'use strict';

  if (window.CollectionGridAnalytics) {
    return;
  }

  class CollectionGridAnalytics {
    constructor() {
      this.queryId = null;
      this.index = null;
      this.isPersonalized = false;
      this.experienceVariant = null;
      this.sort = null;
      this.defaultSort = null;
      this.filterContext = {};
      this.appliedPreferences = [];
      this.shopperSegment = null;
      this.exposureTracked = false;
      
      this.init();
    }

    init() {
      // Wait for DOM and tracker to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      // Delegated click handling works for cards that render later (InstantSearch
      // innerHTML, sort/filter re-renders) and doesn't depend on tracker load order.
      this.bindDelegatedClicks();
      this.loadCollectionContext();

      // Wait for analytics tracker to initialize
      if (!window.MagnoliaAnalyticsTracker) {
        console.warn('[Collection Grid Analytics] MagnoliaAnalyticsTracker not found');
        return;
      }

      this.setupProductCardTracking();
      this.trackExposure();
      this.observeGridChanges();
      
    }

    /**
     * Load collection context from page data
     */
    loadCollectionContext() {
      this.index = this.index || window.__ALGOLIA_CONFIG?.indices?.products || 'shopify_products';
      this.sort = this.sort || 'default';
      this.experienceVariant = this.experienceVariant || 'default';

      // Get Algolia query ID from meta tag or data attribute
      const queryIdMeta = document.querySelector('meta[name="algolia-query-id"]');
      if (queryIdMeta) {
        this.queryId = queryIdMeta.content;
      }

      // Get from collection element data attribute
      const collectionElement = document.querySelector('[data-collection-analytics]');
      if (collectionElement) {
        const data = collectionElement.dataset;
        this.queryId = this.queryId || data.queryId;
        this.index = data.index || this.index;
        this.isPersonalized = data.isPersonalized === 'true';
        this.experienceVariant = data.experienceVariant || 'default';
        this.defaultSort = data.sort || null; // fallback only; live sort wins (getActiveSort)
        
        if (data.appliedPreferences) {
          try {
            this.appliedPreferences = JSON.parse(data.appliedPreferences);
          } catch {
            console.warn('[Collection Grid Analytics] Failed to parse appliedPreferences');
          }
        }

        if (data.shopperSegment) {
          this.shopperSegment = data.shopperSegment;
        }
      }

      // Active sort: live UI/URL first, data-sort attribute as fallback
      this.sort = this.getActiveSort();

      // Get active filters
      this.updateFilterContext();

    }

    /**
     * Sort method that is active right now. Read at the moment it is needed
     * (click / exposure) so it never depends on a change listener having fired:
     *   1. the sort <select> value (what the shopper sees, updates instantly)
     *   2. the sort_by URL param (e.g. a shared link before the select syncs)
     *   3. the data-sort attribute on [data-collection-analytics]
     *   4. 'default'
     */
    getActiveSort() {
      const select = document.querySelector('#SortBy, [name="sort_by"]');
      if (select && select.value) return select.value;

      const fromUrl = new URL(window.location.href).searchParams.get('sort_by');
      if (fromUrl) return fromUrl;

      return this.defaultSort || 'default';
    }

    /**
     * Update filter context from active facets
     */
    updateFilterContext() {
      const filterContext = {};
      
      // Get active filters from URL or UI
      const url = new URL(window.location.href);
      const params = url.searchParams;

      // Common filter parameters
      const filterParams = ['filter.p.vendor', 'filter.p.tag', 'filter.v.price', 'filter.v.option'];
      
      filterParams.forEach(param => {
        if (params.has(param)) {
          const value = params.getAll(param);
          filterContext[param] = value.length === 1 ? value[0] : value;
        }
      });

      // Get from facet elements
      const activeFacets = document.querySelectorAll('.active-facets__button');
      if (activeFacets.length > 0) {
        filterContext.active_facet_count = activeFacets.length;
      }

      this.filterContext = filterContext;
    }

    /**
     * Per-card listeners are no longer used (see bindDelegatedClicks).
     * Kept as a no-op so sort/filter hooks that call it keep working.
     */
    setupProductCardTracking() {}

    get cardSelector() {
      return '.card-wrapper, .product-card, [data-product-card], .hit-wrapper, [data-product-handle], [data-object-id]';
    }

    /**
     * Outermost card containing el (cards can match the selector at several nesting levels)
     */
    getOuterCard(el) {
      let card = el.closest(this.cardSelector);
      while (card && card.parentElement && card.parentElement.closest(this.cardSelector)) {
        card = card.parentElement.closest(this.cardSelector);
      }
      return card;
    }

    /**
     * 1-based position of a card in the grid
     */
    getCardPosition(card) {
      const explicit = parseInt(card.dataset.position, 10);
      if (!isNaN(explicit) && explicit > 0) return explicit;

      const cards = Array.from(document.querySelectorAll(this.cardSelector)).filter(
        (el) => !(el.parentElement && el.parentElement.closest(this.cardSelector))
      );
      const idx = cards.indexOf(card);
      return idx === -1 ? 1 : idx + 1;
    }

    /**
     * Resolve the live Algolia search that produced this card, if any.
     * Uses the same window.AlgoliaInsights registry the search page uses, so the
     * click carries the real queryID, index and absolute position.
     */
    lookupSearchRecord(card, productId) {
      try {
        const api = window.AlgoliaInsights;
        if (!api || typeof api.findSearchFor !== 'function') return null;

        const objectID = card.dataset.objectId || productId;
        const record = api.findSearchFor(objectID, true);
        if (!record) return null;

        const position =
          typeof api.getPositionOf === 'function' ? api.getPositionOf(record, objectID) : null;

        return {
          queryId: record.queryID || undefined,
          index: record.indexName || undefined,
          position: Number(position) > 0 ? Number(position) : null,
        };
      } catch {
        return null;
      }
    }

    /**
     * One document-level listener for all product cards (left + middle click).
     * Enter on a focused link fires a native click, so no keydown handler is
     * needed (it used to double-count).
     */
    bindDelegatedClicks() {
      if (this.clicksBound) return;
      // The search page has its own click tracking (algolia-instantsearch.js)
      if (window.location.pathname === '/search') return;
      this.clicksBound = true;

      const handler = (event) => {
        if (event.type === 'auxclick' && event.button !== 1) return;
        const target = event.target;
        if (!target || !target.closest) return;

        // Quick view / carousel controls are not product-page clicks
        if (target.closest('.quick-add, .quick-add-modal, modal-opener, .hit-carousel__arrow, .hit-carousel__dot')) return;

        const link = target.closest('a[href*="/products/"]');
        if (!link) return;

        const card = this.getOuterCard(link);
        if (!card) return; // e.g. header/menu product links

        const productId = this.extractProductId(card, link);
        if (!productId) {
          console.warn('[Collection Grid Analytics] Could not extract product ID from card', card);
          return;
        }

        const record = this.lookupSearchRecord(card, productId);

        this.trackProductClick({
          productId,
          productName: this.extractProductName(card),
          productCategory: this.extractProductCategory(card),
          productPrice: this.extractProductPrice(card),
          productBrand: this.extractProductBrand(card),
          clickPosition: (record && record.position) || this.getCardPosition(card),
          queryId: record ? record.queryId : undefined,
          index: record ? record.index : undefined,
        });
      };

      document.addEventListener('click', handler, true);
      document.addEventListener('auxclick', handler, true);
    }

    /**
     * Extract product ID from card: data attributes, then form, then the
     * handle from the product URL (Shopify URLs use handles, not numeric IDs).
     */
    extractProductId(card, link = null) {
      if (card.dataset.productId) return card.dataset.productId;
      if (card.dataset.objectId) return card.dataset.objectId;

      const form = card.querySelector('form[data-product-id]');
      if (form) return form.dataset.productId;

      const a = link || card.querySelector('a[href*="/products/"]');
      if (a) {
        const match = a.pathname.match(/\/products\/([^/?#]+)/);
        if (match) return decodeURIComponent(match[1]);
      }

      if (card.dataset.productHandle) return card.dataset.productHandle;
      return null;
    }

    /**
     * Extract product name from card
     */
    extractProductName(card) {
      const titleElement = card.querySelector('.card__heading, .card-information__text, h3, [data-product-title]');
      return titleElement ? titleElement.textContent.trim() : 'Unknown Product';
    }

    /**
     * Extract product category from page or card
     */
    extractProductCategory(card) {
      // Try from collection title
      const collectionTitle = document.querySelector('.collection-hero__title, .collection__title, h1');
      if (collectionTitle) {
        return collectionTitle.textContent.trim();
      }

      // Try from card data attribute
      if (card.dataset.productType) {
        return card.dataset.productType;
      }

      return 'Uncategorized';
    }

    /**
     * Extract product price from card
     */
    extractProductPrice(card) {
      const priceElement = card.querySelector('.price-item--regular, .price__regular, [data-product-price]');
      if (priceElement) {
        const priceText = priceElement.textContent.trim();
        const match = priceText.match(/[\d,]+\.\d{2}/);
        if (match) {
          return parseFloat(match[0].replace(',', ''));
        }
      }
      return null;
    }

    /**
     * Extract product brand from card or page
     */
    extractProductBrand(card) {
      // Try from card
      const vendorElement = card.querySelector('.card__vendor, [data-product-vendor]');
      if (vendorElement) {
        return vendorElement.textContent.trim();
      }

      // Try from meta tag
      const brandMeta = document.querySelector('meta[property="og:brand"]');
      if (brandMeta) {
        return brandMeta.content;
      }

      return null;
    }

    /**
     * Track product click
     */
    trackProductClick(productData) {
      if (!window.MagnoliaAnalyticsTracker) {
        console.warn('[Collection Grid Analytics] Tracker not available');
        return;
      }

      // Update filter context before tracking (may have changed)
      this.updateFilterContext();

      const clickData = {
        productId: productData.productId,
        productName: productData.productName,
        productCategory: productData.productCategory,
        productPrice: productData.productPrice,
        productBrand: productData.productBrand,
        clickPosition: productData.clickPosition,
        queryId: productData.queryId || this.queryId || undefined,
        index: productData.index || this.index,
        resultType: 'collection',
        sort: this.getActiveSort(),
        isPersonalized: this.isPersonalized,
        experienceVariant: this.experienceVariant,
        filterContext: Object.keys(this.filterContext).length > 0 ? this.filterContext : null,
        appliedPreferences: this.appliedPreferences.length > 0 ? this.appliedPreferences : null,
      };

      // Dispatch custom event (picked up by analytics tracker)
      const event = new CustomEvent('analytics:product-click', {
        detail: clickData,
        bubbles: true,
      });
      document.dispatchEvent(event);

    }

    /**
     * Total number of products in the (filtered) collection, not just the cards
     * on the current page. Tries, in order:
     *   1. data-result-count on [data-collection-analytics]
     *   2. known count elements (custom + Dawn-style ids/classes)
     *   3. a "<n> products/items/results" text scan inside the main content
     * Handles thousands separators ("1,234"). Returns 0 if nothing is found.
     */
    getResultCount() {
      const toNumber = (text) => {
        const m = String(text || '').match(/\d[\d,]*/);
        return m ? parseInt(m[0].replace(/,/g, ''), 10) : null;
      };

      const collectionElement = document.querySelector('[data-collection-analytics]');
      if (collectionElement && collectionElement.dataset.resultCount) {
        const n = toNumber(collectionElement.dataset.resultCount);
        if (n !== null) return n;
      }

      const selectors = [
        '[data-result-count]',
        '.collection-product-count',
        '#ProductCount',
        '#ProductCountDesktop',
        '.product-count__text',
        '.product-count',
        '[data-product-count]',
        '[data-total-count]',
      ];
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (!el) continue;
        const n = toNumber(el.dataset.resultCount || el.textContent);
        if (n !== null) return n;
      }

      // Last resort: leaf element in main content reading like "48 products"
      const root = document.querySelector('#MainContent, main') || document.body;
      const pattern = /^\s*(\d[\d,]*)\s*(products?|items?|results?)\s*$/i;
      const leaf = Array.from(root.querySelectorAll('*')).find(
        (el) => el.children.length === 0 && pattern.test(el.textContent)
      );
      if (leaf) return toNumber(leaf.textContent);

      console.warn('[Collection Grid Analytics] Result count element not found; reporting 0');
      return 0;
    }

    /**
     * Track personalization exposure
     */
    trackExposure() {
      if (this.exposureTracked) return;
      if (!window.MagnoliaAnalyticsTracker) return;

      // Total products in the collection (with current filters applied)
      const resultCount = this.getResultCount();

      // If no query ID, this is a standard collection (not Algolia-powered)
      // Still track exposure but mark as untracked variant
      const exposureData = {
        isPersonalized: this.isPersonalized,
        appliedPreferences: this.appliedPreferences,
        shopperSegment: this.shopperSegment,
        experienceVariant: this.queryId ? this.experienceVariant : 'untracked',
        queryId: this.queryId || 'no-query-id',
        index: this.index || window.location.pathname,
        resultType: 'collection',
        resultCount: resultCount,
        sort: this.getActiveSort(),
        filterContext: Object.keys(this.filterContext).length > 0 ? this.filterContext : null,
      };

      // Dispatch exposure event
      const event = new CustomEvent('analytics:personalization-exposure', {
        detail: exposureData,
        bubbles: true,
      });
      document.dispatchEvent(event);

      this.exposureTracked = true;

    }

    /**
     * Observe grid changes (sorting, filtering, infinite scroll)
     */
    observeGridChanges() {
      // Re-setup tracking when sort/filter changes
      const sortSelect = document.querySelector('#SortBy, [name="sort_by"]');
      if (sortSelect) {
        sortSelect.addEventListener('change', () => {
          setTimeout(() => {
            const chosenSort = sortSelect.value;
            this.exposureTracked = false;
            this.loadCollectionContext();
            this.sort = chosenSort || this.getActiveSort();
            this.setupProductCardTracking();
            this.trackExposure();
          }, 500); // Wait for grid to update
        });
      }

      // Watch for facet changes
      const facetForm = document.querySelector('facet-filters-form, #FacetFiltersForm');
      if (facetForm) {
        facetForm.addEventListener('submit', () => {
          setTimeout(() => {
            this.exposureTracked = false;
            this.loadCollectionContext();
            this.setupProductCardTracking();
            this.trackExposure();
          }, 500);
        });
      }

      // Infinite scroll / re-rendered cards need no observer: clicks are delegated.
    }
  }

  // Initialize
  window.CollectionGridAnalytics = new CollectionGridAnalytics();

})();